import { accountFingerprint, fromBase64Url, openLetter, randomBytes, sealLetter, sha256, toBase64Url, utf8, type AccountKeys, type Correspondent } from '@realmery/vault';
import { fogged as isFogged } from '@realmery/realm-core';
import { realms } from '../db/client.ts';
import { realmFile, type FoggedPictures, type SharedCopy } from '../db/protocol.ts';
import { keyIdOf, openEditionKey, sealEditionKey, sealedUnder } from '../db/sealed.ts';
import { renderFoggedJpeg } from '../export/mapPicture.ts';
import { thumbnailOf } from '../canvas/mapImage.ts';
import { AccountError, api } from './api.ts';
import { mySharingKeys } from './store.ts';

/**
 * Sharing a realm (docs/SHARING-PLAN.md; the server's side is server/src/routes/sharing.ts).
 *
 * - **Members and invitations** (section 3.3): an invitation carries a letter from the
 *   owner to the member, sealed and signed in the owner's browser, with the realm's name,
 *   the role and the key of the edition the role reads. The server sees neither the name
 *   nor the key, and can't change the role unnoticed.
 * - **Editions** (sections 3.1 and 3.4): for each role the realm is shared with, the
 *   owner's browser makes a copy with everything above that audience taken out, seals it
 *   under the edition's key and uploads it; Update players' copy does it again. The owner
 *   keeps each edition's key in a letter to themself, so any of their devices can update it.
 * - **Reading** (section 3.4): a member's browser downloads their edition, checks it opens
 *   with the key from the letter, and keeps it as a read-only realm of its own.
 * - **Co-owners** (docs/SHARING-CO-OWNERS-PLAN.md section 3): their letter carries the
 *   realm key itself. They download the owner's realm, and write it back, taking turns
 *   with an edit lease. When one goes, the owner's browser seals the realm under a new key,
 *   and its editions too.
 * - **Co-owners' copies** (section 7): each edition's key is also kept sealed under the
 *   realm key, so a co-owner can make an existing edition again under it. New editions,
 *   new keys and members' letters stay the owner's.
 */

/** Co-owner ('editor'), Player and Reader. Contributor is no longer offered. */
export type ShareRole = 'editor' | 'player' | 'reader';
/** The roles that read a copy of their own: an edition. */
export type ReadingRole = 'player' | 'reader';
export const SHARE_ROLES: readonly { value: ShareRole; label: string; description: string }[] = [
  { value: 'editor', label: 'Co-owner', description: 'Sees and changes everything, taking turns with you. Only you share the realm or delete it.' },
  { value: 'player', label: 'Player', description: 'Sees what the realm shows players: no GM-only notes, pins or regions.' },
  { value: 'reader', label: 'Reader', description: 'Sees only what the realm shows readers, for a published world.' },
];
export const READING_ROLES = SHARE_ROLES.filter((r): r is (typeof SHARE_ROLES)[number] & { value: ReadingRole } => r.value !== 'editor');

export function roleLabel(role: string): string {
  return SHARE_ROLES.find((r) => r.value === role)?.label ?? role.charAt(0).toUpperCase() + role.slice(1);
}

/** What the letter to a member says. `key` is their edition's key, base64url. */
interface MemberLetter {
  realm: string;
  role: string;
  name: string;
  key?: string;
}

/** What the owner's letter to themself about an edition says. */
interface EditionLetter {
  realm: string;
  audience: string;
  key: string;
}

/** Someone to share with, found by email. */
export interface FoundAccount extends Correspondent {
  readonly email: string;
}

/** A person a realm is shared with, as its owner sees them. */
export interface Member extends FoundAccount {
  readonly role: string;
  readonly status: 'invited' | 'member';
  readonly invitedAt: number;
  readonly joinedAt: number | null;
  /** Theirs, to compare with what they see on their account page. */
  readonly fingerprint: string;
}

/** The edition a member's role reads, as the server lists it. */
export interface EditionInfo {
  readonly version: number;
  readonly size: number;
  readonly updatedAt: number;
}

/** A realm shared with this account, or an invitation to one. */
export interface Share {
  readonly owner: FoundAccount & { readonly fingerprint: string };
  readonly realmId: string;
  readonly role: string;
  readonly status: 'invited' | 'member';
  readonly invitedAt: number;
  readonly joinedAt: number | null;
  /**
   * The realm's name, from the owner's letter. Null when the letter doesn't open, or says
   * another realm or role than the server does: the share is then shown as a warning.
   */
  readonly name: string | null;
  /** The edition for this account's role, if the owner has made one. */
  readonly edition: EditionInfo | null;
  /** A Co-owner's: the owner's realm itself, once they've accepted. */
  readonly realm: EditionInfo | null;
  /** That edition's key (a Co-owner's: the realm key), from the letter. */
  readonly key: string | null;
}

// ---- requests with a body of bytes, which api.ts doesn't send ----

async function bytesCall(method: 'GET' | 'PUT', path: string, init: { body?: Uint8Array; headers?: Record<string, string> } = {}): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(`/api/${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(method === 'GET' ? {} : { 'x-realmery-request': '1', 'content-type': 'application/octet-stream' }),
        ...init.headers,
      },
      ...(init.body ? { body: init.body as Uint8Array<ArrayBuffer> } : {}),
    });
  } catch {
    throw new AccountError('offline', 'Could not reach Realmery. Check your connection.');
  }
  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new AccountError(json.error ?? 'server_error', json.message ?? 'That did not work.', response.status);
  }
  return response;
}

const hex = (bytes: Uint8Array) => [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');

// ---- the owner's side ----

export function lookUp(email: string): Promise<FoundAccount> {
  return api.post<{ account: FoundAccount }>('sharing/lookup', { email }).then((r) => r.account);
}

function myCard(userId: string, keys: AccountKeys): Correspondent {
  return { userId, exchangePublic: keys.record.exchangePublic, signingPublic: keys.record.signingPublic };
}

/** The realm's editions on the server, each with its key from the owner's letter to themself (null if that doesn't open). */
async function editionsOf(
  realmId: string,
  userId: string,
  keys: AccountKeys,
): Promise<Map<string, { version: number; key: Uint8Array<ArrayBuffer> | null; rekeyDue: boolean; sealedKey: string | null }>> {
  const { editions } = await api.get<{ editions: { audience: string; version: number; ownerLetter: string; rekeyDue: boolean; sealedKey: string | null }[] }>(
    `sharing/realms/${realmId}/editions`,
  );
  const out = new Map<string, { version: number; key: Uint8Array<ArrayBuffer> | null; rekeyDue: boolean; sealedKey: string | null }>();
  for (const e of editions) {
    let key: Uint8Array<ArrayBuffer> | null = null;
    try {
      const said = (await openLetter(keys, userId, myCard(userId, keys), e.ownerLetter)) as Partial<EditionLetter> | null;
      if (said?.realm === realmId && said.audience === e.audience && typeof said.key === 'string') key = fromBase64Url(said.key);
    } catch {
      // A letter that doesn't open: the edition is made again under a new key.
    }
    out.set(e.audience, { version: e.version, key, rekeyDue: e.rekeyDue, sealedKey: e.sealedKey });
  }
  return out;
}

/** The realm's fogged maps' pictures, with the fog painted in, as players see them: for its editions. */
async function foggedPictures(file: string): Promise<FoggedPictures> {
  const data = await realms.exportData(file);
  const fogged: Record<string, { image: Uint8Array; thumbnail: Uint8Array | null }> = {};
  for (const m of data.atlas.maps) {
    if (!isFogged(m, false)) continue;
    const image = await renderFoggedJpeg(m);
    if (image.length === 0) throw new AccountError('fog', `The map “${m.map.name}” couldn’t be drawn with its fog, so the copy wasn’t made.`);
    fogged[m.map.id] = { image, thumbnail: await thumbnailOf(image).catch(() => null) };
  }
  return fogged;
}

/** The realm's key, for sealing its editions' keys (section 7). The caller zeroes it. */
async function realmKeyOf(file: string): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array((await realms.realmKeyRaw(file)).key);
}

/**
 * Makes and uploads the edition of the realm (`file`, this account's) for an audience:
 * the fog painted into its maps' pictures here, then filtered and sealed by the worker.
 * Its key goes up with it, in a letter to this account and sealed under the realm key.
 */
async function publish(
  file: string,
  realmId: string,
  audience: ReadingRole,
  key: Uint8Array<ArrayBuffer>,
  base: number,
  newKey: boolean,
  userId: string,
  keys: AccountKeys,
  realmKey: Uint8Array<ArrayBuffer>,
): Promise<void> {
  const { bytes } = await realms.buildEdition(file, audience, await foggedPictures(file), key);
  const ownerLetter = await sealLetter(keys, userId, myCard(userId, keys), { realm: realmId, audience, key: toBase64Url(key) } satisfies EditionLetter);
  await bytesCall('PUT', `sharing/realms/${realmId}/editions/${audience}?base=${base}`, {
    body: bytes,
    headers: {
      'x-content-sha256': hex(await sha256(bytes as Uint8Array<ArrayBuffer>)),
      'x-owner-letter': ownerLetter,
      'x-sealed-key': await sealEditionKey(realmKey, userId, realmId, audience, key),
      ...(newKey ? { 'x-new-key': '1' } : {}),
    },
  });
}

/**
 * Opens the copy an audience would get of this account's realm, made now and kept nowhere
 * (docs/SHARING-CO-OWNERS-PLAN.md section 2). Returns the preview's realm file.
 */
export async function openPreview(file: string, audience: ReadingRole): Promise<string> {
  return (await realms.openPreview(file, audience, await foggedPictures(file))).file;
}

function memberLetter(keys: AccountKeys, userId: string, to: Pick<Correspondent, 'userId' | 'exchangePublic'>, realmId: string, role: string, name: string, key: Uint8Array) {
  return sealLetter(keys, userId, to, { realm: realmId, role, name, key: toBase64Url(key) } satisfies MemberLetter);
}

/** What a realm's sharing needs to change its editions: the realm, and who reads it now. */
export interface Shared {
  readonly file: string;
  readonly realmId: string;
  readonly realmName: string;
  readonly people: readonly Member[];
}

/**
 * Makes sure each audience has an edition with a key no one who left holds (section 3.3):
 * one is made where there's none, and made again under a new key where someone could
 * read it who may no longer (removed, left, another role), with the letters of everyone
 * who reads it written again. `refresh` makes every one again, as Update players' copy
 * does. Returns each audience's key.
 */
async function ensureEditions(shared: Shared, audiences: readonly ReadingRole[], refresh: boolean): Promise<{ keys: Map<string, Uint8Array<ArrayBuffer>>; rekeyed: number }> {
  const { userId, keys } = await mySharingKeys();
  const editions = await editionsOf(shared.realmId, userId, keys);
  const keyFor = new Map<string, Uint8Array<ArrayBuffer>>();
  const relettered = new Set<string>();
  let rekeyed = 0;
  const realmKey = await realmKeyOf(shared.file);
  try {
    for (const audience of new Set(audiences)) {
      const existing = editions.get(audience);
      const fresh = !existing?.key || existing.rekeyDue;
      const key = fresh ? randomBytes(32) : existing.key!;
      if (fresh || refresh) await publish(shared.file, shared.realmId, audience, key, existing?.version ?? 0, fresh, userId, keys, realmKey);
      if (fresh && existing) rekeyed++;
      if (fresh || refresh) relettered.add(audience);
      keyFor.set(audience, key);
    }
  } finally {
    realmKey.fill(0);
  }
  const letters = await Promise.all(
    shared.people
      .filter((m) => relettered.has(m.role))
      .map(async (m) => ({ memberId: m.userId, role: m.role, letter: await memberLetter(keys, userId, m, shared.realmId, m.role, shared.realmName, keyFor.get(m.role)!) })),
  );
  if (letters.length > 0) await api.post(`sharing/realms/${shared.realmId}/letters`, { letters });
  return { keys: keyFor, rekeyed };
}

const readingRoles = (people: readonly Member[]) => [...new Set(people.map((m) => m.role))].filter((r): r is ReadingRole => r === 'player' || r === 'reader');

/** The key a role's letter carries: a Co-owner's is the realm key itself, a Player's or Reader's their edition's. */
async function keyForRole(shared: Shared, role: ShareRole): Promise<Uint8Array<ArrayBuffer>> {
  if (role === 'editor') return new Uint8Array((await realms.realmKeyRaw(shared.file)).key);
  return (await ensureEditions(shared, [role], false)).keys.get(role)!;
}

/**
 * Seals the realm under a new key, once a co-owner went (SHARING-CO-OWNERS-PLAN.md
 * section 3.2), and writes the new key to the co-owners who remain. The realm is uploaded
 * under the new key first, so their letters never name a key the server's copy lacks.
 */
async function rekeyRealm(shared: Shared): Promise<void> {
  const { userId, keys } = await mySharingKeys();
  const raw = new Uint8Array((await realms.rekeyRealm(shared.file)).key);
  try {
    const letters = await Promise.all(
      shared.people
        .filter((m) => m.role === 'editor')
        .map(async (m) => ({ memberId: m.userId, role: m.role, letter: await memberLetter(keys, userId, m, shared.realmId, m.role, shared.realmName, raw) })),
    );
    await api.post(`sharing/realms/${shared.realmId}/letters`, { letters, realmRekeyed: true });
  } finally {
    raw.fill(0);
  }
}

/** Told when this account's realms are shared with someone new, or with one less: the app bar's role says how many. */
const peopleChanged = new Set<() => void>();
export function onPeopleChanged(listener: () => void): () => void {
  peopleChanged.add(listener);
  return () => peopleChanged.delete(listener);
}
const tellPeopleChanged = () => peopleChanged.forEach((listener) => listener());

export async function invite(shared: Shared, to: FoundAccount, role: ShareRole): Promise<void> {
  const { userId, keys } = await mySharingKeys();
  const key = await keyForRole(shared, role);
  const letter = await memberLetter(keys, userId, to, shared.realmId, role, shared.realmName, key);
  await api.post(`sharing/realms/${shared.realmId}/members`, { memberId: to.userId, role, letter });
  tellPeopleChanged();
  // A new co-owner can update the copies from the start.
  if (role === 'editor') await sealEditionKeys({ ...shared, people: [...shared.people, { ...to, role } as Member] });
}

/**
 * After someone in `role` goes from the realm: what they could read gets a new key. A
 * Player's or Reader's edition only if someone still reads it. For a Co-owner, the realm
 * itself always, as the owner's own devices still need it, then the editions they knew
 * the keys of, under the new realm key, for `readers` (who reads each now: by default
 * everyone left; with a co-owner become a Player or Reader, them too).
 */
async function rekeyAfter(shared: Shared, role: string, readers: readonly Member[] = shared.people): Promise<void> {
  if (role === 'editor') {
    await rekeyRealm(shared);
    await ensureEditions({ ...shared, people: readers }, readingRoles(readers), false);
    return;
  }
  if (shared.people.some((m) => m.role === role)) await ensureEditions(shared, [role as ReadingRole], false);
}

/** A new role: they get the new role's key, and the old role's edition (or the realm) gets a new one, which they don't. */
export async function changeRole(shared: Shared, member: Member, role: ShareRole): Promise<void> {
  const { userId, keys } = await mySharingKeys();
  const key = await keyForRole(shared, role);
  const letter = await memberLetter(keys, userId, member, shared.realmId, role, shared.realmName, key);
  await api.post(`sharing/realms/${shared.realmId}/members/${member.userId}`, { role, letter });
  tellPeopleChanged();
  const others = shared.people.filter((m) => m.userId !== member.userId);
  await rekeyAfter({ ...shared, people: others }, member.role, [...others, { ...member, role }]);
  if (role === 'editor') await sealEditionKeys({ ...shared, people: [...others, { ...member, role }] });
}

/** Removes a member (or withdraws an invitation), then gives what they read a new key for whoever still reads it. */
export async function removeMember(shared: Shared, member: Member): Promise<void> {
  await api.del<unknown>(`sharing/realms/${shared.realmId}/members/${member.userId}`);
  tellPeopleChanged();
  await rekeyAfter({ ...shared, people: shared.people.filter((m) => m.userId !== member.userId) }, member.role);
}

/**
 * Update players' copy (decision 7.4): every edition the realm's members read made again
 * from the realm as it is now, and each member's letter written again with the realm's
 * name as it is now. Returns how many editions were updated.
 */
export async function updateCopies(shared: Shared): Promise<number> {
  const roles = readingRoles(shared.people);
  await ensureEditions(shared, roles, true);
  return roles.length;
}

/**
 * New keys for editions someone left since (or was removed from elsewhere), and for the
 * realm when a co-owner went (`realmDue`): how many were made again.
 */
export async function rekeyDue(shared: Shared, realmDue = false): Promise<number> {
  // The realm first, so the editions' keys are sealed under its new key.
  if (realmDue) await rekeyRealm(shared);
  const editions = (await ensureEditions(shared, readingRoles(shared.people), false)).rekeyed;
  return editions + (realmDue ? 1 : 0);
}

/**
 * Seals under the realm key each edition key that isn't yet, or is under a key the realm
 * no longer has, so co-owners can update the copies (section 7): for realms shared
 * before, and the realm's own re-keys. Editions due a new key are left for the owner to make again.
 */
export async function sealEditionKeys(shared: Shared): Promise<void> {
  if (!shared.people.some((m) => m.role === 'editor')) return;
  const { userId, keys } = await mySharingKeys();
  const editions = await editionsOf(shared.realmId, userId, keys);
  const realmKey = await realmKeyOf(shared.file);
  try {
    const current = await keyIdOf(realmKey);
    for (const [audience, e] of editions) {
      if (!e.key || e.rekeyDue || (e.sealedKey && sealedUnder(e.sealedKey) === current)) continue;
      const sealedKey = await sealEditionKey(realmKey, userId, shared.realmId, audience, e.key);
      await api.post(`sharing/realms/${shared.realmId}/editions/${audience}/sealed-key`, { version: e.version, sealedKey }).catch((error: unknown) => {
        // Updated from elsewhere in between: sealed the next time.
        if (!(error instanceof AccountError && error.code === 'conflict')) throw error;
      });
    }
  } finally {
    realmKey.fill(0);
  }
}

/** Who the realm is shared with, and when its editions were last updated. A realm that isn't backed up is shared with no one, and says so. */
export async function members(
  realmId: string,
): Promise<{ members: Member[]; updatedAt: number | null; rekeyDue: boolean; realmRekeyDue: boolean } | { notBackedUp: true }> {
  try {
    const { members: rows, realmRekeyDue } = await api.get<{ members: Omit<Member, 'fingerprint'>[]; realmRekeyDue: boolean }>(`sharing/realms/${realmId}/members`);
    const { editions } = await api.get<{ editions: { audience: string; updatedAt: number; rekeyDue: boolean }[] }>(`sharing/realms/${realmId}/editions`);
    return {
      members: await Promise.all(rows.map(async (m) => ({ ...m, fingerprint: await accountFingerprint(m.userId, m) }))),
      updatedAt: editions.length === 0 ? null : Math.min(...editions.map((e) => e.updatedAt)),
      // Someone left or was removed while their edition still has readers: it needs a new key.
      rekeyDue: editions.some((e) => e.rekeyDue && rows.some((m) => m.role === e.audience)),
      // A co-owner went: the realm needs a new key.
      realmRekeyDue,
    };
  } catch (error) {
    if (error instanceof AccountError && error.code === 'not_backed_up') return { notBackedUp: true };
    throw error;
  }
}

/** How many people each of this account's shared realms is shared with, by realm id. */
export async function sharedRealms(): Promise<ReadonlyMap<string, number>> {
  const { realms: rows } = await api.get<{ realms: { realmId: string; people: number }[] }>('sharing/realms');
  return new Map(rows.map((r) => [r.realmId, r.people]));
}

// ---- the member's side ----

/** The realm file a shared realm is kept in here: a uuid from the owner and realm, so it's the same on every device. */
export async function shareFile(ownerId: string, realmId: string): Promise<string> {
  const bytes = (await sha256(utf8(`realmery-share-1\0${ownerId}\0${realmId}`))).slice(0, 16);
  bytes[6] = (bytes[6]! & 0x0f) | 0x80; // version 8: made by its own rule
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const h = hex(bytes);
  return realmFile(`${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`);
}

/** Invitations and realms shared with this account, each letter opened and checked against what the server says. */
export async function sharedWithMe(): Promise<Share[]> {
  const { shares } = await api.get<{ shares: (Omit<Share, 'name' | 'owner' | 'key'> & { owner: FoundAccount; letter: string })[] }>('sharing/with-me');
  if (shares.length === 0) return [];
  const { userId, keys } = await mySharingKeys();
  return Promise.all(
    shares.map(async ({ letter, owner, ...share }) => {
      let name: string | null = null;
      let key: string | null = null;
      try {
        const said = (await openLetter(keys, userId, owner, letter)) as Partial<MemberLetter> | null;
        if (said?.realm === share.realmId && said.role === share.role && typeof said.name === 'string') {
          name = said.name;
          key = typeof said.key === 'string' ? said.key : null;
        }
      } catch {
        // Shown as a share that doesn't check out.
      }
      return { ...share, owner: { ...owner, fingerprint: await accountFingerprint(owner.userId, owner) }, name, key };
    }),
  );
}

/** The copies of shared realms kept on this device; those no longer shared with this account (`current`) are forgotten. */
export async function sharedCopies(current?: readonly Share[]): Promise<SharedCopy[]> {
  const copies = await realms.sharedCopies();
  if (!current) return copies;
  const kept = new Set(current.filter((s) => s.status === 'member').map((s) => `${s.owner.userId}/${s.realmId}`));
  const gone = copies.filter((c) => !kept.has(`${c.ownerId}/${c.realmId}`));
  for (const copy of gone) await realms.forgetShared(copy.file);
  return copies.filter((c) => !gone.includes(c));
}

/**
 * Makes sure this device has the newest copy of a shared realm, downloading it if the
 * owner updated it, and returns its file to open. Offline, the copy here opens as it is.
 */
export async function fetchShared(share: Share): Promise<string> {
  const file = await shareFile(share.owner.userId, share.realmId);
  if (share.role === 'editor') return fetchCoOwned(share, file);
  const here = (await realms.sharedCopies()).find((c) => c.file === file);
  const fresh = here && share.edition && here.version >= share.edition.version && here.role === share.role;
  if (fresh || (here && !share.edition)) return file;
  if (!share.edition || !share.key) throw new AccountError('no_edition', `${share.owner.email} hasn’t sent a copy of this realm yet.`);
  const response = await bytesCall('GET', `sharing/with-me/${share.owner.userId}/${share.realmId}/edition`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (hex(await sha256(bytes)) !== response.headers.get('x-content-sha256')) {
    throw new AccountError('damaged', 'The copy was damaged on the way. Try again.');
  }
  await realms.storeShared({
    file,
    ownerId: share.owner.userId,
    ownerEmail: share.owner.email,
    realmId: share.realmId,
    role: share.role,
    name: share.name ?? 'Shared realm',
    version: Number(response.headers.get('x-edition-version') ?? share.edition.version),
    key: fromBase64Url(share.key),
    bytes,
  });
  return file;
}

/**
 * A co-owned realm (SHARING-CO-OWNERS-PLAN.md section 3.3): the owner's realm itself,
 * downloaded when there's a newer version, or a new key in the letter, and this device
 * has nothing of its own waiting to be sent.
 */
async function fetchCoOwned(share: Share, file: string): Promise<string> {
  if (!share.realm || !share.key) throw new AccountError('no_realm', `This realm can’t be opened: ask ${share.owner.email} to share it with you again.`);
  const raw = fromBase64Url(share.key);
  const here = await realms.coOwnedState(file);
  if (here && (here.dirty || (here.keyId === (await keyIdOf(raw)) && here.base >= share.realm.version))) return file;
  const response = await bytesCall('GET', `realms/${share.realmId}?owner=${encodeURIComponent(share.owner.userId)}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (hex(await sha256(bytes)) !== response.headers.get('x-content-sha256')) {
    throw new AccountError('damaged', 'The realm was damaged on the way. Try again.');
  }
  await realms.storeShared({
    file,
    ownerId: share.owner.userId,
    ownerEmail: share.owner.email,
    realmId: share.realmId,
    role: 'editor',
    name: share.name ?? 'Shared realm',
    version: Number(response.headers.get('x-realm-version') ?? share.realm.version),
    key: raw,
    bytes,
  });
  return file;
}

/** Who a co-owned realm is shared with, as its co-owners see it (SHARING-CO-OWNERS-PLAN.md section 3.1), and its editions (section 7). */
export interface PeopleOfRealm {
  readonly owner: { readonly email: string };
  readonly members: readonly { readonly email: string; readonly role: string; readonly status: 'invited' | 'member'; readonly you: boolean }[];
  readonly editions: readonly { readonly audience: string; readonly version: number; readonly updatedAt: number; readonly rekeyDue: boolean; readonly sealedKey: string | null }[];
}

export function peopleOfRealm(ownerId: string, realmId: string): Promise<PeopleOfRealm> {
  return api.get<PeopleOfRealm>(`sharing/with-me/${ownerId}/${realmId}/members`);
}

/**
 * Update players' copy, by a co-owner (SHARING-CO-OWNERS-PLAN.md section 7): this
 * device's unsent changes sent first, then each edition the realm's members read made
 * again from the realm as it is here, under the key it has. Returns the roles updated,
 * and those only the owner can make now: no copy yet, one due a new key, or a key not
 * yet sealed for co-owners.
 */
export async function updateCoOwnedCopies(copy: Pick<SharedCopy, 'ownerId' | 'realmId' | 'ownerEmail'>): Promise<{ updated: ReadingRole[]; ownerMust: ReadingRole[] }> {
  const share = (await sharedWithMe()).find((s) => s.owner.userId === copy.ownerId && s.realmId === copy.realmId);
  if (share?.role !== 'editor' || share.status !== 'member' || !share.key) throw new AccountError('no_realm', `Only ${copy.ownerEmail} can send copies of this realm.`);
  const file = await shareFile(share.owner.userId, share.realmId);
  await realms.sendNow(file);
  const people = await peopleOfRealm(share.owner.userId, share.realmId);
  const roles = [...new Set(people.members.map((m) => m.role))].filter((r): r is ReadingRole => r === 'player' || r === 'reader');
  const realmKey = fromBase64Url(share.key);
  const updated: ReadingRole[] = [];
  const ownerMust: ReadingRole[] = [];
  try {
    for (const role of roles) {
      const edition = people.editions.find((e) => e.audience === role);
      const key =
        edition && !edition.rekeyDue && edition.sealedKey ? await openEditionKey(realmKey, share.owner.userId, share.realmId, role, edition.sealedKey) : null;
      if (!edition || !key) {
        ownerMust.push(role);
        continue;
      }
      try {
        const { bytes } = await realms.buildEdition(file, role, await foggedPictures(file), key);
        await bytesCall('PUT', `sharing/with-me/${share.owner.userId}/${share.realmId}/editions/${role}?base=${edition.version}`, {
          body: bytes,
          headers: { 'x-content-sha256': hex(await sha256(bytes as Uint8Array<ArrayBuffer>)) },
        });
        updated.push(role);
      } catch (error) {
        if (error instanceof AccountError && error.code === 'owner_only') ownerMust.push(role);
        else throw error;
      } finally {
        key.fill(0);
      }
    }
  } finally {
    realmKey.fill(0);
  }
  return { updated, ownerMust };
}

// ---- the edit lease (SHARING-CO-OWNERS-PLAN.md section 3.4) ----

/** What taking the lease said: not needed (no co-owners), this session's, or someone else's until `expiresAt`. */
export type Lease =
  | { readonly needed: false }
  | { readonly needed: true; readonly mine: true; readonly expiresAt: number }
  | { readonly needed: true; readonly mine: false; readonly holder: { readonly email: string; readonly device: string }; readonly expiresAt: number };

/** Takes or renews the realm's lease for this session, unless someone else holds it. */
export function takeLease(ownerId: string, realmId: string): Promise<Lease> {
  return api.post<Lease>(`sharing/lease/${ownerId}/${realmId}`);
}

/** Gives the lease up. `leaving`: the page is going, so the request is sent even as it closes. */
export function releaseLease(ownerId: string, realmId: string, leaving = false): Promise<void> {
  if (leaving) {
    void fetch(`/api/sharing/lease/${ownerId}/${realmId}`, { method: 'DELETE', credentials: 'same-origin', keepalive: true, headers: { 'x-realmery-request': '1' } }).catch(
      () => undefined,
    );
    return Promise.resolve();
  }
  return api.del<unknown>(`sharing/lease/${ownerId}/${realmId}`).then(() => undefined);
}

export function accept(share: Share): Promise<void> {
  return api.post(`sharing/with-me/${share.owner.userId}/${share.realmId}/accept`).then(() => undefined);
}

/** Declines an invitation, or leaves a shared realm, forgetting its copy here. */
export async function leave(share: Share): Promise<void> {
  await api.del<unknown>(`sharing/with-me/${share.owner.userId}/${share.realmId}`);
  await realms.forgetShared(await shareFile(share.owner.userId, share.realmId)).catch(() => undefined);
}

// ---- fingerprints checked (section 3.2) ----

/** What a letter to oneself about a check says: whose fingerprint matched, and what it was. */
interface CheckLetter {
  contact: string;
  fingerprint: string;
}

/** The fingerprints this account has compared and found to match, by account id. */
export async function contactChecks(): Promise<Map<string, string>> {
  const { checks } = await api.get<{ checks: { contactId: string; letter: string }[] }>('sharing/checks');
  if (checks.length === 0) return new Map();
  const { userId, keys } = await mySharingKeys();
  const out = new Map<string, string>();
  for (const c of checks) {
    try {
      const said = (await openLetter(keys, userId, myCard(userId, keys), c.letter)) as Partial<CheckLetter> | null;
      if (said?.contact === c.contactId && typeof said.fingerprint === 'string') out.set(c.contactId, said.fingerprint);
    } catch {
      // Not this account's letter: as if never checked.
    }
  }
  return out;
}

export async function markChecked(contactId: string, fingerprint: string): Promise<void> {
  const { userId, keys } = await mySharingKeys();
  const letter = await sealLetter(keys, userId, myCard(userId, keys), { contact: contactId, fingerprint } satisfies CheckLetter);
  await api.post(`sharing/checks/${contactId}`, { letter });
}

export function unmarkChecked(contactId: string): Promise<void> {
  return api.del<unknown>(`sharing/checks/${contactId}`).then(() => undefined);
}
